import React from 'react';
import { Link } from 'react-router-dom';
import _get from 'lodash/get';
import classnames from 'classnames';
import { useLocation } from 'react-router';

import Typography from '@material-ui/core/Typography';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import ShortTextIcon from 'components/Icons/ShortText';
import HyperLinkIcon from 'components/Icons/HyperLink';
import SingleSelectIcon from 'components/Icons/SingleSelect';
import LongTextIcon from 'components/Icons/LongText';
import EmailIcon from 'components/Icons/Email';
import PhoneIcon from 'components/Icons/Phone';
import MessageAddressIcon from 'components/Icons/MessageAddress';
import FreeFormChipsIcon from 'components/Icons/FreeFormChips';
import MultiSelectIcon from 'components/Icons/MultiSelect';
import AutocompleteIcon from 'components/Icons/Autocomplete';
import LinkCollectionsIcon from 'components/Icons/LinkCollections';
import DatesIcon from 'components/Icons/Dates';
import NumberIcon from 'components/Icons/Number';
import BooleanIcon from 'components/Icons/Boolean';
import SkillsIcon from 'components/Icons/Skills';
import EducationIcon from 'components/Icons/Education';
import ExperienceIcon from 'components/Icons/Experience';
import LanguageIcon from 'components/Icons/Language';
import ReachOutToMeForIcon from 'components/Icons/ReachOutToMeFor';
import PageIcon from 'components/Icons/Page';
import PageCollectionIcon from 'components/Icons/PageCollection';

import makeStyles from '@material-ui/core/styles/makeStyles';

interface Props {
  className?: string;
  item: {
    Icon: any;
    title: string;
    description: string;
    link: string;
  };
  link: boolean;
  parentObjectKey?: string | string[];
  onClick?: () => void;
}

export const attributeTypes: {
  [key: string]: { title: string; Icon: any; description: string; link: string };
} = {
  text: {
    title: 'Short Text',
    Icon: ShortTextIcon,
    link: 'text',
    description: 'A short piece of text displayed on a single line.',
  },
  textLong: {
    title: 'Long Text',
    Icon: LongTextIcon,
    link: 'textLong',
    description: 'A multi-line text area for longer form content.',
  },
  textSelect: {
    title: 'Select',
    Icon: SingleSelectIcon,
    link: 'textSelect',
    description:
      'A short piece of text that has a preset list of options to choose from. Only one option can be selected.',
  },
  hyperlink: {
    title: 'Hyperlink',
    Icon: HyperLinkIcon,
    link: 'hyperlink',
    description: 'A link out to an external website.',
  },
  email: {
    title: 'Email',
    Icon: EmailIcon,
    link: 'email',
    description: 'An email address that will open an email client when clicked.',
  },
  phone: {
    title: 'Phone Number',
    Icon: PhoneIcon,
    link: 'phone',
    description: 'A phone number that will call when clicked.',
  },
  messageAddress: {
    title: 'Message Address',
    Icon: MessageAddressIcon,
    link: 'messageAddress',
    description:
      'A button that will open an external messaging tool when clicked. This attribute must be mapped to a data source.',
  },
  textCollection: {
    title: 'Text Collection',
    Icon: FreeFormChipsIcon,
    link: 'textCollection',
    description:
      'A list of short text items that will search when clicked. If the attribute is editable, entry will be free-form.',
  },
  multiSelect: {
    title: 'Multi Select',
    Icon: MultiSelectIcon,
    link: 'multiSelect',
    description:
      'A restricted text collection attribute that has a preset list of options to choose from.',
  },
  autocompleteTextCollection: {
    title: 'Autocomplete Chips',
    Icon: AutocompleteIcon,
    link: 'autocompleteTextCollection',
    description:
      'A text collection attribute that has a longer list of options to choose from and can also support free-form entry.',
  },
  date: {
    title: 'Date',
    Icon: DatesIcon,
    link: 'date',
    description: 'A Date that has a calendar input and can be searched by range.',
  },
  number: {
    title: 'Number',
    Icon: NumberIcon,
    link: 'number',
    description: 'An integer with restricted number input.',
  },
  boolean: {
    title: 'Boolean',
    Icon: BooleanIcon,
    link: 'boolean',
    description: 'A Yes or No prompt.',
  },
  linkCollection: {
    title: 'Link Collection',
    Icon: LinkCollectionsIcon,
    link: 'linkCollection',
    description:
      'A collection of custom hyperlinks that can be used for documents, social accounts, or websites to share with others.',
  },
  skills: {
    title: 'Skills',
    Icon: SkillsIcon,
    link: 'collection',
    description:
      'A detailed list of self-assessed skills allowing specification of level and certain skills to be featured. Utilizes a defined database of over 40,000 skills.',
  },
  education: {
    title: 'Education',
    Icon: EducationIcon,
    link: 'education',
    description:
      'A detailed list of past education, including school, degree type, majors, minors, concentrations, and class year.',
  },
  experience: {
    title: 'Experience',
    Icon: ExperienceIcon,
    link: 'experience',
    description:
      'A detailed list of professional experience to be used for past work or volunteer positions.',
  },
  languages: {
    title: 'Language',
    Icon: LanguageIcon,
    link: 'languages',
    description:
      'A detailed list of languages including proficiency level and skillsets in reading, writing, speaking, and understanding.',
  },
  reachOutToMeFor: {
    title: 'Reach Out to Me For',
    Icon: ReachOutToMeForIcon,
    link: 'reachOutToMeFor',
    description:
      'A collection of generic or categorized items used to define which professional things someone should be contacted about.',
  },
  page: {
    title: 'Page',
    Icon: PageIcon,
    link: 'page',
    description:
      'Users may only be associated to one page of this type. (Ex: Building Location, Department, etc.)',
  },
  pageCollection: {
    title: 'Page Collection',
    Icon: PageCollectionIcon,
    link: 'pageCollection',
    description:
      'Users may be associated to multiple pages of this type. (Ex: Projects, Employee Resource Groups, etc.)',
  },
  independentPage: {
    title: 'Independent Page',
    Icon: PageCollectionIcon,
    link: '',
    description:
      'Create pages that are not associated to a Person. These pages will only be accessible from Sift search and will not include page members.',
  },
};

function AttributeTypeCard({ className, item, link, parentObjectKey, onClick }: Props) {
  const classes = useStyles();
  const Icon = _get(item, 'Icon');
  const location = useLocation();
  const cardRootClass = classnames(classes.cardRoot, className, {
    [classes.cardRootHover]: link || onClick,
    [classes.marginRight]: link || onClick,
    [classes.clickable]: onClick,
  });

  const card = (
    <Card className={cardRootClass} data-testid={`${item.title}-card`} onClick={onClick}>
      <CardContent classes={{ root: classes.content }}>
        <Typography className={classes.cardTitle} variant="h6">
          {item.title}
        </Typography>
        {Icon && <Icon className={classes.icon} data-testid={`${item.title}-icon`} />}
        <Typography className={classes.cardContent} variant="body2">
          {item.description}
        </Typography>
      </CardContent>
    </Card>
  );

  const cardLink = parentObjectKey
    ? `${location.pathname}/${item.link}?parentObjectKey=${parentObjectKey}`
    : `${location.pathname}/${item.link}`;

  return link ? (
    <Link data-testid={`${item.title}-link`} to={cardLink}>
      {card}
    </Link>
  ) : (
    card
  );
}

const useStyles = makeStyles((theme: any) => ({
  cardRoot: {
    maxWidth: 214,
  },
  cardRootHover: {
    '&:hover': {
      boxShadow:
        '0px 6px 14px -6px rgba(33, 33, 33, 0.12), 0px 10px 32px -4px rgba(33, 33, 33, 0.1)',
    },
  },
  clickable: {
    cursor: 'pointer',
  },
  content: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  marginRight: {
    marginRight: theme.spacing(2),
  },
  cardTitle: {
    padding: theme.spacing(),
  },
  icon: {
    width: 214,
    height: 102,
  },
  cardContent: {
    padding: theme.spacing(1, 1, 1.5, 1),
  },
}));

export default AttributeTypeCard;
